import { DocumentInput } from "@/components/ui/document-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Building2, FileText, Globe } from "lucide-react";

interface CompanyInfoStepProps {
  formData: {
    documentType: "cpf" | "cnpj";
    document: string;
    subdomain: string;
  };
  handleChange: (field: string, value: string) => void;
}

export default function CompanyInfoStep({ formData, handleChange }: CompanyInfoStepProps) {
  const preview = (formData.subdomain || "sua-empresa").toLowerCase().replace(/[^a-z0-9-]/g, "");

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <Label className="text-base font-medium">Documento da empresa ou titular</Label>
        <p className="text-sm text-muted-foreground">
          Escolha CPF (pessoa física) ou CNPJ (empresa).
        </p>
        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-muted/60 border border-border/60">
          {(["cpf", "cnpj"] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleChange("documentType", type)}
              className={cn(
                "relative rounded-lg py-3 px-3 text-sm font-medium transition-all",
                formData.documentType === type
                  ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="flex items-center justify-center gap-2">
                <FileText className="h-4 w-4 opacity-70" />
                {type === "cpf" ? "CPF" : "CNPJ"}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="document" className="text-base font-medium">
          {formData.documentType === "cpf" ? "CPF" : "CNPJ"}
        </Label>
        <div className="relative">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <DocumentInput
            id="document"
            type={formData.documentType}
            value={formData.document}
            onChange={(value) => handleChange("document", value)}
            className="pl-10 h-11"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="subdomain" className="text-base font-medium">
            Subdomínio do seu espaço
          </Label>
          <p className="text-sm text-muted-foreground">
            Será o endereço que você e sua equipe usarão para acessar o painel.
          </p>
        </div>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            id="subdomain"
            placeholder="ex: minha-empresa"
            value={formData.subdomain ?? ""}
            onChange={(e) => handleChange("subdomain", e.target.value.toLowerCase().replace(/\s/g, "-"))}
            className="pl-10 h-11 font-mono text-sm"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="rounded-lg border border-dashed border-primary/25 bg-primary/5 px-4 py-3 text-sm">
          <div className="flex items-start gap-2 text-muted-foreground">
            <Globe className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <div>
              <span className="font-medium text-foreground">Seu acesso:</span>{" "}
              <span className="font-mono text-foreground break-all">
                {preview || "sua-empresa"}.baitaticket.com.br
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
